import { baseApi } from './baseApi';
import type { JsonObject, JsonValue } from './baseQuery';
import type { AssetAssociationWithDevice } from '../app/components/assets/details/types';
import type { Asset, AssetWithEmbed, AssociatedDevice, Masterdata, Tag } from '../app/components/assets/types';

interface Page<T> {
    items: T[];
    _links?: { next?: { href?: string } };
}

interface UpdateMasterdataArgs {
    assetId: string;
    masterdata: JsonObject;
    updatedMasterdata: Masterdata;
}

interface AssetTagArgs {
    assetId: string;
    tagId: string;
}

interface DeleteAssociationArgs {
    associationId: string;
    assetId: string;
}

const toJsonObject = (value: object): JsonObject => JSON.parse(JSON.stringify(value)) as JsonObject;

const removeEmptyValues = (value: JsonValue): JsonValue | undefined => {
    if (value === null) {
        return undefined;
    }

    if (Array.isArray(value)) {
        const values = value.map(removeEmptyValues).filter((item): item is JsonValue => item !== undefined);
        return values.length > 0 ? values : undefined;
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value)
            .map(([key, item]) => [key, removeEmptyValues(item)] as const)
            .filter((entry): entry is readonly [string, JsonValue] => entry[1] !== undefined);
        return entries.length > 0 ? Object.fromEntries(entries) : undefined;
    }

    return value;
};

const getJsonDifference = (previous: JsonValue, next: JsonValue): JsonValue | undefined => {
    if (
        typeof previous === 'object' &&
        previous !== null &&
        !Array.isArray(previous) &&
        typeof next === 'object' &&
        next !== null &&
        !Array.isArray(next)
    ) {
        const entries = new Set([...Object.keys(previous), ...Object.keys(next)]);
        const difference = Array.from(entries)
            .map(key => [key, getJsonDifference(previous[key] ?? null, next[key] ?? null)] as const)
            .filter((entry): entry is readonly [string, JsonValue] => entry[1] !== undefined);
        return difference.length > 0 ? Object.fromEntries(difference) : undefined;
    }

    return JSON.stringify(previous) === JSON.stringify(next) ? undefined : next;
};

export const getMasterdataUpdate = (previous: Masterdata | null, next: Masterdata): JsonObject => {
    const previousValue = removeEmptyValues(toJsonObject(previous ?? {})) ?? {};
    const nextValue = removeEmptyValues(toJsonObject(next)) ?? {};
    const difference = getJsonDifference(previousValue, nextValue);

    return difference && !Array.isArray(difference) && typeof difference === 'object' ? difference : {};
};

const getNextLink = <T>(page: Page<T>) => page._links?.next?.href;

const getItems = <T>(page: Page<T>) => (Array.isArray(page.items) ? page.items : []);

const mapAsset = (asset: AssetWithEmbed): Asset => ({
    id: asset.id,
    account_id: asset.account_id,
    name: asset.name,
    identification: asset.identification,
    identification_type: asset.identification_type,
    type: asset.type,
    status: asset.status,
    brand: asset.brand,
    license_plate: asset.license_plate,
    license_plate_country_code: asset.license_plate_country_code,
    tags: asset._embedded?.tags?.items.map(tag => tag.id) ?? [],
    masterdata: asset._embedded?.master_data ?? null,
});

const mapAssociatedDevices = (associations: AssetAssociationWithDevice[]): AssociatedDevice[] =>
    associations
        .filter(association => association._embedded !== undefined)
        .map(association => ({ device: association._embedded.device, associationId: association.id }));

const assetUpdateBody = (asset: Asset): JsonObject =>
    toJsonObject({
        id: asset.id,
        account_id: asset.account_id,
        name: asset.name,
        identification: asset.identification,
        identification_type: asset.identification_type,
        type: asset.type,
        status: asset.status,
        brand: asset.brand,
        license_plate: asset.license_plate,
        license_plate_country_code: asset.license_plate_country_code,
    });

const updateCachedAsset = (cachedAsset: Asset, asset: Asset) => {
    cachedAsset.name = asset.name;
    cachedAsset.identification = asset.identification;
    cachedAsset.identification_type = asset.identification_type;
    cachedAsset.type = asset.type;
    cachedAsset.status = asset.status;
    cachedAsset.brand = asset.brand;
    cachedAsset.license_plate = asset.license_plate;
    cachedAsset.license_plate_country_code = asset.license_plate_country_code;
};

export const hasAssetChanges = (originalAsset: Asset, updatedAsset: Asset) =>
    updatedAsset.name !== originalAsset.name ||
    (originalAsset.license_plate
        ? updatedAsset.license_plate !== originalAsset.license_plate
        : updatedAsset.license_plate !== null && updatedAsset.license_plate.length > 0) ||
    updatedAsset.license_plate_country_code !== originalAsset.license_plate_country_code ||
    updatedAsset.status !== originalAsset.status ||
    (!originalAsset.identification && updatedAsset.identification !== null && updatedAsset.brand !== null);

// TODO: Validate the paginated asset response before mapping it.
const mapAssetPage = (data: JsonValue | null): Page<AssetWithEmbed> => data as unknown as Page<AssetWithEmbed>;

// TODO: Validate the paginated association response before mapping it.
const mapAssociationPage = (data: JsonValue | null): Page<AssetAssociationWithDevice> =>
    data as unknown as Page<AssetAssociationWithDevice>;

// TODO: Validate the paginated tag response before mapping it.
const mapTagPage = (data: JsonValue | null): Page<Tag> => data as unknown as Page<Tag>;

export const assetAdministrationApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getAssets: build.query<Asset[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const assets: Asset[] = [];
                let nextLink: string | undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? { absoluteUrl: nextLink }
                            : { service: 'assetAdmin', path: '/assets?embed=(tags,master_data)' }
                    );
                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    const page = mapAssetPage(result.data);
                    assets.push(...getItems(page).map(mapAsset));
                    nextLink = getNextLink(page);
                } while (nextLink);

                return { data: assets };
            },
            providesTags: result => [
                { type: 'AssetList', id: 'LIST' },
                ...(result?.map(asset => ({ type: 'Asset' as const, id: asset.id })) ?? []),
            ],
        }),
        getAssociations: build.query<AssetAssociationWithDevice[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const associations: AssetAssociationWithDevice[] = [];
                let nextLink: string | undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? { absoluteUrl: nextLink }
                            : { service: 'assetAdmin', path: '/associations?embed=(device)' }
                    );
                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    const page = mapAssociationPage(result.data);
                    associations.push(...getItems(page));
                    nextLink = getNextLink(page);
                } while (nextLink);

                return { data: associations };
            },
            providesTags: result => [
                { type: 'AssetAssociations', id: 'ALL' },
                ...(result?.map(association => ({ type: 'Association' as const, id: association.id })) ?? []),
            ],
        }),
        getAssetAssociations: build.query<AssociatedDevice[], string>({
            async queryFn(assetId, _api, _extraOptions, baseQuery) {
                const associations: AssetAssociationWithDevice[] = [];
                let nextLink: string | undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? { absoluteUrl: nextLink }
                            : {
                                  service: 'assetAdmin',
                                  path: `/associations?asset_id=${encodeURIComponent(assetId)}&embed=(device)`,
                              }
                    );
                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    const page = mapAssociationPage(result.data);
                    associations.push(...getItems(page));
                    nextLink = getNextLink(page);
                } while (nextLink);

                return { data: mapAssociatedDevices(associations) };
            },
            providesTags: (_result, _error, assetId) => [{ type: 'AssetAssociations', id: assetId }],
        }),
        getTags: build.query<Tag[], string | null>({
            async queryFn(accountId, _api, _extraOptions, baseQuery) {
                const tags: Tag[] = [];
                let nextLink: string | undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? { absoluteUrl: nextLink }
                            : { service: 'tagService', path: '/tags', params: { account_id: accountId } }
                    );
                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    const page = mapTagPage(result.data);
                    tags.push(...getItems(page));
                    nextLink = getNextLink(page);
                } while (nextLink);

                return { data: tags };
            },
            providesTags: (result, _error, accountId) => [
                { type: 'TagList', id: accountId ?? 'ALL' },
                ...(result?.map(tag => ({ type: 'Tag' as const, id: tag.id })) ?? []),
            ],
        }),
        updateAsset: build.mutation<void, Asset>({
            query: asset => ({
                service: 'assetAdmin',
                path: `/assets/${asset.id}`,
                method: 'PUT',
                body: assetUpdateBody(asset),
            }),
            invalidatesTags: (_result, _error, asset) =>
                asset.status === 'archived' ? [{ type: 'AssetAssociations' as const, id: asset.id }] : [],
            async onQueryStarted(asset, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        assetAdministrationApi.util.updateQueryData('getAssets', undefined, assets => {
                            const cachedAsset = assets.find(currentAsset => currentAsset.id === asset.id);
                            if (cachedAsset) {
                                updateCachedAsset(cachedAsset, asset);
                            }
                        })
                    );
                } catch {}
            },
        }),
        deleteAsset: build.mutation<void, string>({
            query: assetId => ({ service: 'assetAdmin', path: `/assets/${assetId}`, method: 'DELETE' }),
            invalidatesTags: (_result, _error, assetId) => [
                { type: 'AssetAssociations', id: assetId },
                { type: 'AssetTags', id: assetId },
            ],
            async onQueryStarted(assetId, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        assetAdministrationApi.util.updateQueryData('getAssets', undefined, assets => {
                            const assetIndex = assets.findIndex(asset => asset.id === assetId);
                            if (assetIndex !== -1) {
                                assets.splice(assetIndex, 1);
                            }
                        })
                    );
                } catch {}
            },
        }),
        updateMasterdata: build.mutation<void, UpdateMasterdataArgs>({
            queryFn: async ({ assetId, masterdata }, _api, _extraOptions, baseQuery) => {
                if (Object.keys(masterdata).length === 0) {
                    return { data: undefined };
                }

                const result = await baseQuery({
                    service: 'assetAdmin',
                    path: `/assets/${assetId}/masterdata`,
                    method: 'PATCH',
                    body: masterdata,
                });

                if (result.error) {
                    return { error: result.error, meta: result.meta };
                }

                return { data: undefined, meta: result.meta };
            },
            invalidatesTags: () => [],
            async onQueryStarted({ assetId, updatedMasterdata }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        assetAdministrationApi.util.updateQueryData('getAssets', undefined, assets => {
                            const cachedAsset = assets.find(asset => asset.id === assetId);
                            if (cachedAsset) {
                                cachedAsset.masterdata = updatedMasterdata;
                            }
                        })
                    );
                } catch {}
            },
        }),
        deleteAssociation: build.mutation<void, DeleteAssociationArgs>({
            query: ({ associationId }) => ({
                service: 'assetAdmin',
                path: `/associations/${associationId}`,
                method: 'DELETE',
            }),
            invalidatesTags: () => [],
            async onQueryStarted({ associationId, assetId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        assetAdministrationApi.util.updateQueryData('getAssociations', undefined, associations => {
                            const associationIndex = associations.findIndex(
                                association => association.id === associationId
                            );
                            if (associationIndex !== -1) {
                                associations.splice(associationIndex, 1);
                            }
                        })
                    );
                    dispatch(
                        assetAdministrationApi.util.updateQueryData(
                            'getAssetAssociations',
                            assetId,
                            associatedDevices => {
                                const associationIndex = associatedDevices.findIndex(
                                    associatedDevice => associatedDevice.associationId === associationId
                                );
                                if (associationIndex !== -1) {
                                    associatedDevices.splice(associationIndex, 1);
                                }
                            }
                        )
                    );
                } catch {}
            },
        }),
        createTag: build.mutation<Tag, Tag>({
            query: tag => ({
                service: 'tagService',
                path: `/tags/${tag.id}`,
                method: 'PUT',
                body: toJsonObject(tag),
                headers: { 'If-None-Match': '*' },
            }),
            transformResponse: (_result, _meta, tag) => tag,
            invalidatesTags: () => [],
            async onQueryStarted(tag, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    for (const accountId of [tag.account_id, null]) {
                        dispatch(
                            assetAdministrationApi.util.updateQueryData('getTags', accountId, tags => {
                                if (!tags.some(currentTag => currentTag.id === tag.id)) {
                                    tags.push(tag);
                                }
                            })
                        );
                    }
                } catch {}
            },
        }),
        addAssetTag: build.mutation<void, AssetTagArgs>({
            query: ({ assetId, tagId }) => ({
                service: 'assetAdmin',
                path: `/assets/${assetId}/tags/${tagId}`,
                method: 'PUT',
                body: { id: tagId },
            }),
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'AssetTags', id: assetId }],
            async onQueryStarted({ assetId, tagId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        assetAdministrationApi.util.updateQueryData('getAssets', undefined, assets => {
                            const cachedAsset = assets.find(asset => asset.id === assetId);
                            if (cachedAsset && !cachedAsset.tags.includes(tagId)) {
                                cachedAsset.tags.push(tagId);
                            }
                        })
                    );
                } catch {}
            },
        }),
        removeAssetTag: build.mutation<void, AssetTagArgs>({
            query: ({ assetId, tagId }) => ({
                service: 'assetAdmin',
                path: `/assets/${assetId}/tags/${tagId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'AssetTags', id: assetId }],
            async onQueryStarted({ assetId, tagId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        assetAdministrationApi.util.updateQueryData('getAssets', undefined, assets => {
                            const cachedAsset = assets.find(asset => asset.id === assetId);
                            if (cachedAsset) {
                                cachedAsset.tags = cachedAsset.tags.filter(currentTagId => currentTagId !== tagId);
                            }
                        })
                    );
                } catch {}
            },
        }),
    }),
});

export const {
    useGetAssetsQuery,
    useGetAssociationsQuery,
    useGetAssetAssociationsQuery,
    useGetTagsQuery,
    useUpdateAssetMutation,
    useDeleteAssetMutation,
    useUpdateMasterdataMutation,
    useDeleteAssociationMutation,
    useCreateTagMutation,
    useAddAssetTagMutation,
    useRemoveAssetTagMutation,
} = assetAdministrationApi;

export type { AssetTagArgs, DeleteAssociationArgs, UpdateMasterdataArgs };
